import { Injectable } from '@angular/core';


@Injectable({ providedIn: 'root' })
export class DateUtilityService {
  public static maxDateString: string = '9998-12-31T00:00:00';
  public static maxDate: Date = new Date(DateUtilityService.maxDateString);
  public static nullDateString: string = '1900-01-01T00:00:00';
  public static nullDate: Date = new Date(DateUtilityService.nullDateString);

  //  Returns an approximate calculation of time based on the biggest unit of time.  Can be
  //  overridden to supply a specific unit of time.  Specifying a unit will return the number value.
  public dateDiff(start: Date, end: Date,
    preferredUnit?: 'minutes' | 'hours' | 'days' | 'months' | 'years',
    excludeEndDate?: boolean): string | number {
    const nullDateTime = DateUtilityService.nullDate.getTime();

    //  If neither of these are dates or they are null just return a blank or 0.
    if (!(start instanceof Date) || !(end instanceof Date)
      || (start.getTime() == nullDateTime) || (end.getTime() == nullDateTime)) {
      return !!preferredUnit ? 0 : "";
    }

    if (excludeEndDate) {
      end = new Date(end); //  This should prevent us from changing the endDate that was passed in.
      end.setDate(end.getDate() - 1);
    }

    const ms = Math.abs(end.getTime() - start.getTime());

    //  Base calculations
    const minutes = ms / (1000 * 60);
    const hours = minutes / 60;
    const days = hours / 24;
    const months = days / 30.4375;   // average
    const years = days / 365.2425;  // average
    const isPast = start < end;

    //  If we force a specific unit return the value as a number
    if (!!preferredUnit) {
      let value: number;

      switch (preferredUnit) {
        case 'minutes':
          value = minutes;
          break;
        case 'hours':
          value = hours;
          break;
        case 'days':
          value = days;
          break;
        case 'months':
          value = months;
          break;
        case 'years':
          value = years;
          break;
      }

      value = value < 1 ? 0 : Math.round(value);

      if (isPast)
        value = -value;

      return value;
    }

    //  Otherwise auto‑select best unit
    const oneOrMore = (value: number, unit: string) => `${value} ${unit}${value != 1 ? 's' : ''}`;
    let returnString = '';

    if (minutes < 60) {
      if (minutes == 0)
        return 'Now';

      returnString = oneOrMore(Math.round(minutes), 'minute');
    }
    else if (hours < 24)
      returnString = oneOrMore(Math.round(hours), 'hour');
    else if (days < 30)
      returnString = oneOrMore(Math.round(days), 'day');
    else if (months < 12)
      returnString = oneOrMore(Math.round(months), 'month');
    else
      returnString = oneOrMore(Math.round(years), 'year');

    if (isPast)
      returnString += ' ago';

    return returnString;
  }

  //  Checks to see if date string is considered empty.
  public isEmptyDate(date: string | null | undefined) {
    return ((date == null) || (date.trim() == "") || (date == DateUtilityService.nullDateString))
  }

  // Checks to see if date is a valid Date type.
  public isValidDate(date: Date | null | undefined): boolean {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }

    return true;
  }
}

// Return date and time without time zone as a string
export function toLocalDateTimeString(date: Date | string): string {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
}

// Used primarily for HR position changes to store date changes as YYYY-MM-DD.  Should not be used in the UI.
export function toSimpleDateString(date: Date) {
  return date.toLocaleDateString('sv-SE')
}

//  Standard date/time formatting in the UI.
export function toStandardDate(date: Date) {
  return date.toLocaleDateString('en-US', { month: "short", day: "numeric", year: 'numeric' });
}

export function toStandardTime(date: Date) {
  return date.toLocaleTimeString('en-US', { hour: "2-digit", minute: "2-digit" });
}

export function toStandardDateTime(date: Date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) +
    ' ' + date.toLocaleTimeString('en-US', { hour: "2-digit", minute: "2-digit" });
}

export function toStandardMonthYear(date: Date) {
  return date.toLocaleDateString('en-US', { month: "short", year: 'numeric' });
}
